import { z } from 'zod';
import { isValidPhone, normalizePhone } from './phone.js';
/* ------------------------------------------------------------------ *
 * Primitives
 * ------------------------------------------------------------------ */
export const phoneSchema = z
    .string()
    .transform(normalizePhone)
    .refine(isValidPhone, { message: 'شماره موبایل معتبر نیست.' });
export const idSchema = z.coerce.number().int().positive();
/** Sheets hand back "TRUE"/"FALSE"/1/0 as often as real booleans. */
export const sheetBool = z
    .union([z.boolean(), z.string(), z.number(), z.null(), z.undefined()])
    .transform((v) => {
    if (typeof v === 'boolean')
        return v;
    if (typeof v === 'number')
        return v !== 0;
    const s = String(v ?? '').trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes' || s === 'بله';
});
export const priceSchema = z.coerce.number().int().min(0).max(1_000_000_000_000);
export const stockSchema = z.coerce.number().int().min(0).max(1_000_000);
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
/* ------------------------------------------------------------------ *
 * Catalogue
 * ------------------------------------------------------------------ */
export const productAttributeSchema = z.object({
    key: z.string().min(1).max(120),
    value: z.string().max(2000),
});
export const productCodeSchema = z
    .string()
    .trim()
    .min(2)
    .max(80)
    .regex(/^0\d+$/, 'کد محصول باید فقط عدد باشد و با صفر شروع شود.');
export const productTrackingLinkSchema = z.object({
    siteId: z.coerce.number().int().positive(),
    url: z.string().trim().url('لینک رهگیری معتبر نیست.').max(1000),
});
export const trackingSiteWriteSchema = z.object({
    name: z.string().trim().min(2).max(160),
    baseUrl: z.string().trim().url('آدرس سایت معتبر نیست.').max(1000).optional().nullable(),
    priceUnit: z.enum(['toman', 'rial']).default('toman'),
    isActive: z.boolean().default(true),
});
export const productWriteSchema = z.object({
    productId: productCodeSchema,
    parentProductId: z.string().trim().max(80).optional().nullable(),
    sku: z.string().trim().max(80).optional().nullable(),
    title: z.string().trim().min(1).max(400),
    model: z.string().trim().max(400).optional().nullable(),
    categoryName: z.string().trim().max(160).optional().nullable(),
    brandName: z.string().trim().max(160).optional().nullable(),
    color: z.string().trim().max(160).optional().nullable(),
    colorEn: z.string().trim().max(160).optional().nullable(),
    colorCode: z.string().trim().max(32).optional().nullable(),
    price: priceSchema.default(0),
    costPrice: priceSchema.default(0),
    oldPrice: priceSchema.optional().nullable(),
    discount: z.coerce.number().int().min(0).max(100).default(0),
    stock: stockSchema.default(0),
    kermanStock: stockSchema.optional().nullable(),
    tehranStock: stockSchema.optional().nullable(),
    otherStocks: z.record(z.string(), z.number()).default({}),
    warranty: z.string().trim().max(300).optional().nullable(),
    sellType: z.string().trim().max(200).optional().nullable(),
    seller: z.string().trim().max(200).optional().nullable(),
    promotion: sheetBool.default(false),
    status: z.enum(['active', 'inactive']).default('active'),
    subTitle: z.string().trim().max(400).optional().nullable(),
    description: z.string().optional().nullable(),
    keywords: z.string().trim().max(500).optional().nullable(),
    slug: z.string().trim().max(200).optional().nullable(),
    ribbon: z.string().trim().max(50).optional().nullable(),
    type: z.string().trim().max(50).default('physical'),
    weight: z.coerce.number().int().default(0),
    dimensions: z.string().trim().max(100).optional().nullable(),
    tracking: z.boolean().default(true),
    imageUrl: z.string().trim().max(1000).optional().nullable(),
    gallery: z.array(z.string().max(1000)).max(12).default([]),
    attributes: z.array(productAttributeSchema).max(60).default([]),
    bundleItems: z.array(z.object({
        productId: z.string().trim().min(1).max(80),
        qty: z.coerce.number().int().min(1).max(1000)
    })).max(200).default([]),
    digikalaLink: z.string().trim().max(1000).optional().nullable(),
    targetSiteUrl: z.string().trim().max(1000).optional().nullable(),
    trackingLinks: z.array(productTrackingLinkSchema).max(3, 'برای هر محصول حداکثر سه سایت قابل رهگیری است.').default([]),
    sortOrder: z.coerce.number().int().default(0),
});
export const productPatchSchema = productWriteSchema.partial();
export const categoryWriteSchema = z.object({
    name: z.string().trim().min(1).max(160),
    faName: z.string().trim().min(1).max(160),
    iconUrl: z.string().trim().max(1000).optional().nullable(),
    brandNames: z.array(z.string().trim().max(160)).max(200).default([]),
    sortOrder: z.coerce.number().int().default(0),
});
export const brandWriteSchema = z.object({
    name: z.string().trim().min(1).max(160),
    faName: z.string().trim().min(1).max(160),
    iconUrl: z.string().trim().max(1000).optional().nullable(),
    sortOrder: z.coerce.number().int().default(0),
});
export const colorWriteSchema = z.object({
    code: z.string().trim().min(1).max(32),
    name: z.string().trim().max(160).optional().nullable(),
    faName: z.string().trim().max(160).optional().nullable(),
});
export const catalogQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    category: z.string().trim().max(160).optional(),
    brands: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .transform((v) => (v == null ? [] : Array.isArray(v) ? v : v.split(',')))
        .pipe(z.array(z.string().trim().min(1)).max(50)),
    promotion: z.coerce.boolean().optional(),
    basketOnly: z.union([
        z.boolean(),
        z.enum(['true', 'false']).transform((value) => value === 'true'),
    ]).optional(),
    creditOnly: z.coerce.boolean().optional(),
    inStock: z.union([
        z.boolean(),
        z.enum(['true', 'false']).transform((value) => value === 'true'),
    ]).default(true),
    sort: z.enum(['price_asc', 'price_desc', 'newest', 'title']).default('price_asc'),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(120).default(24),
});
/* ------------------------------------------------------------------ *
 * Auth
 * ------------------------------------------------------------------ */
export const otpRequestSchema = z.object({ phone: phoneSchema });
export const otpVerifySchema = z.object({
    phone: phoneSchema,
    code: z
        .string()
        .transform((v) => v.replace(/\D/g, ''))
        .pipe(z.string().min(4).max(8)),
});
export const passwordLoginSchema = z.object({
    phone: phoneSchema,
    password: z.string().min(1, 'رمز عبور الزامی است'),
});
export const setPasswordSchema = z.object({
    oldPassword: z.string().optional(),
    newPassword: z.string().min(6, 'رمز عبور باید حداقل ۶ کاراکتر باشد'),
});
export const PROFILE_FIELDS = [
    'name',
    'lastName',
    'storeName',
    'landline',
    'address',
    'postalCode',
    'certificateFileUrl',
    'activity',
    'pageWebsite',
];
export const REQUIRED_PROFILE_FIELDS = ['name', 'lastName', 'storeName', 'address'];
export const PROFILE_FIELD_LABELS = {
    name: 'نام',
    lastName: 'نام خانوادگی',
    storeName: 'نام فروشگاه',
    landline: 'تلفن ثابت',
    address: 'آدرس',
    postalCode: 'کد پستی',
    certificateFileUrl: 'جواز کسب',
    activity: 'نوع فعالیت',
    pageWebsite: 'آدرس پیج / وبسایت',
};
export const profileWriteSchema = z.object({
    name: z.string().trim().max(120).default(''),
    lastName: z.string().trim().max(120).default(''),
    storeName: z.string().trim().max(200).default(''),
    landline: z.string().trim().max(40).default(''),
    address: z.string().trim().max(1000).default(''),
    postalCode: z.string().trim().max(20).default(''),
    certificateFileUrl: z.string().trim().max(1000).default(''),
    activity: z.string().trim().max(120).default(''),
    pageWebsite: z.string().trim().max(255).default(''),
});
/* ------------------------------------------------------------------ *
 * Orders
 * ------------------------------------------------------------------ */
export const ORDER_STATUSES = [
    'new',
    'confirmed',
    'preparing',
    'shipped',
    'delivered',
    'cancelled',
];
export const ORDER_STATUS_LABELS = {
    new: 'در انتظار تایید (موجودی رزرو)',
    confirmed: 'تایید شده',
    preparing: 'در حال آماده‌سازی',
    shipped: 'ارسال شده',
    delivered: 'تحویل شده',
    cancelled: 'لغو شده',
};
export const WAREHOUSES = ['kerman', 'tehran', 'site'];
export const WAREHOUSE_LABELS = {
    kerman: 'انبار کرمان',
    tehran: 'انبار تهران',
    site: 'موجودی سایت',
};
export const orderItemInputSchema = z.object({
    productId: z.string().trim().min(1).max(80),
    warehouse: z.enum(WAREHOUSES).default('kerman'),
    qty: z.coerce.number().int().min(1).max(10_000),
});
export const orderCreateSchema = z.object({
    items: z.array(orderItemInputSchema).min(1).max(200),
    address: z.string().trim().max(1000).optional(),
    paymentMethod: z.string().trim().max(100).optional(),
    paymentPlanId: z.string().trim().max(120).optional(),
    discountCode: z.string().trim().max(40).optional(),
    note: z.string().trim().max(1000).optional(),
    attribution: z.object({
        source: z.string().trim().max(120).optional(),
        medium: z.string().trim().max(120).optional(),
        campaign: z.string().trim().max(200).optional(),
        referrer: z.string().trim().max(1000).optional(),
        landingPage: z.string().trim().max(1000).optional(),
    }).optional(),
});
export const orderItemsReplaceSchema = z.object({
    items: z.array(orderItemInputSchema).min(1, 'سفارش باید حداقل یک قلم داشته باشد').max(200),
});
export const orderPatchSchema = z.object({
    status: z.enum(ORDER_STATUSES).optional(),
    paymentStatus: z.enum(['paid', 'unpaid', 'pending']).optional(),
    paymentMethod: z.string().trim().max(100).optional(),
    note: z.string().trim().max(1000).optional(),
});
/* ------------------------------------------------------------------ *
 * Admin: users, settings, sync
 * ------------------------------------------------------------------ */
export const userPatchSchema = z.object({
    name: z.string().trim().max(120).optional(),
    lastName: z.string().trim().max(120).optional(),
    storeName: z.string().trim().max(200).optional(),
    landline: z.string().trim().max(40).optional(),
    address: z.string().trim().max(1000).optional(),
    postalCode: z.string().trim().max(20).optional(),
    certificateFileUrl: z.string().trim().max(1000).optional(),
    activity: z.string().trim().max(120).optional(),
    pageWebsite: z.string().trim().max(255).optional(),
    isActive: z.boolean().optional(),
    role: z.enum(['customer', 'admin', 'operator']).optional(),
    accessGroupId: z.number().int().positive().nullable().optional(),
});
export const settingWriteSchema = z.object({
    key: z.string().trim().min(1).max(120),
    value: z.string().max(4000).default(''),
});
export const SYNC_ENTITIES = ['products', 'categories', 'brands', 'colors', 'users', 'orders', 'settings'];
export const SYNC_ENTITY_LABELS = {
    products: 'محصولات',
    categories: 'دسته‌بندی‌ها',
    brands: 'برندها',
    colors: 'رنگ‌ها',
    users: 'کاربران',
    orders: 'سفارش‌ها',
    settings: 'تنظیمات',
};
export const syncRunSchema = z.object({
    direction: z.enum(['pull', 'push', 'both']).default('both'),
    entities: z.array(z.enum(SYNC_ENTITIES)).default([...SYNC_ENTITIES]),
    dryRun: z.boolean().default(false),
});
/* ------------------------------------------------------------------ *
 * Uploads
 * ------------------------------------------------------------------ */
export const UPLOAD_KINDS = ['product', 'brand', 'category', 'slide', 'certificate', 'other'];
export const uploadQuerySchema = z.object({
    kind: z.enum(UPLOAD_KINDS).optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(40),
});
/* ------------------------------------------------------------------ *
 * Credit Applications
 * ------------------------------------------------------------------ */
export const CREDIT_STATUSES = ['pending', 'reviewing', 'active', 'action_required'];
export const CREDIT_STATUS_LABELS = {
    pending: 'در صف بررسی',
    reviewing: 'در حال بررسی',
    active: 'تایید شده',
    action_required: 'رد شده',
};
export const CREDIT_STATUS_DESCRIPTIONS = {
    pending: 'درخواست شما دریافت شد، در صف بررسی هستیم.',
    reviewing: 'کارشناسان ما در حال بررسی مدارک مالی شما هستند.',
    active: 'پنل اعتباری شما فعال شد و تایید شده است.',
    action_required: 'متأسفانه درخواست اعتباری شما رد شده است.',
};
export const creditApplicationWriteSchema = z.object({
    nationalId: z.string().trim().min(10, 'کد ملی باید ۱۰ رقم باشد').max(10, 'کد ملی باید ۱۰ رقم باشد'),
    businessType: z.string().trim().min(1, 'نوع کسب‌وکار را انتخاب کنید'),
    nationalCardUrl: z.string().trim().min(1, 'تصویر کارت ملی الزامی است'),
    businessDocsUrl: z.string().trim().min(1, 'تصویر جواز کسب یا اجاره‌نامه الزامی است'),
    checkImageUrl: z.string().trim().min(1, 'تصویر برگ چک صیادی الزامی است'),
    bankStatementUrl: z.string().trim().optional().nullable(),
    referralInfo: z.string().trim().optional().nullable(),
});
export const creditApplicationAdminPatchSchema = z.object({
    status: z.enum(CREDIT_STATUSES),
    assignedCreditLimit: z.coerce.number().min(0).optional(),
    adminCreditScore: z.coerce.number().min(0).max(100).optional(),
    rejectionReason: z.string().trim().optional().nullable(),
    internalNotes: z.string().trim().optional().nullable(),
});
export const CHEQUE_STATUSES = ['pending', 'passed', 'bounced', 'returned'];
export const CHEQUE_STATUS_LABELS = {
    pending: 'در انتظار سررسید',
    passed: 'پاس شده',
    bounced: 'برگشتی',
    returned: 'عودت داده شده',
};
export const creditChequeWriteSchema = z.object({
    orderId: z.coerce.number().int().positive('سفارش چکی را انتخاب کنید'),
    chequeNumber: z.string().trim().min(3, 'شماره چک/صیادی را وارد کنید'),
    bankName: z.string().trim().min(2, 'نام بانک را وارد کنید'),
    accountHolder: z.string().trim().min(2, 'نام صاحب حساب را وارد کنید'),
    imageUrl: z.string().trim().optional().nullable(),
    notes: z.string().trim().optional().nullable(),
});
export const creditChequePatchSchema = z.object({
    status: z.enum(CHEQUE_STATUSES).optional(),
    chequeNumber: z.string().trim().optional(),
    bankName: z.string().trim().optional(),
    accountHolder: z.string().trim().optional(),
    amount: z.coerce.number().int().min(0).optional(),
    dueDate: z.string().trim().optional(),
    notes: z.string().trim().optional().nullable(),
});
export const INSTALLMENT_RECEIPT_STATUSES = ['pending', 'approved', 'rejected'];
export const installmentReceiptWriteSchema = z.object({
    receiptUrl: z.string().trim().min(1, 'تصویر رسید پرداخت را بارگذاری کنید.').refine((value) => value.startsWith('/') || /^https:\/\//i.test(value), 'نشانی فایل رسید معتبر نیست.'),
    trackingCode: z.string().trim().max(120).optional().nullable(),
    note: z.string().trim().max(1000).optional().nullable(),
});
export const installmentReceiptReviewSchema = z.object({
    status: z.enum(INSTALLMENT_RECEIPT_STATUSES),
    rejectionReason: z.string().trim().max(1000).optional().nullable(),
});
//# sourceMappingURL=schemas.js.map